import cx from "classnames";
import React from "react";

import { CollapsedReview } from "@/mk2/apps/strollers/components/CollapsedReview";
import { StrollerEntity, StrollerUserReviewEntity } from "@/mk2/apps/strollers/schemas";
import { strollersAddReviewUrl, strollersReviewsUrl } from "@/mk2/apps/strollers/urls";
import { Btn } from "@/mk2/components/Btn";
import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import {
    STROLLER_ADD_REVIEW,
    STROLLER_MORE_REVIEWS,
    STROLLER_NO_REVIEWS,
    STROLLER_REVIEWS_FROM_MOTHERS,
} from "@/mk/autogenerated/translations/StrollerDetailReviews.663dee7fd7f30c692d419bdf3fffacf7";

import styles from "./StrollerDetailReviews.mscss";

interface OwnProps {
    className?: string;
    stroller: StrollerEntity;
    reviews: StrollerUserReviewEntity[]; // schvalene recenzie kocika 'stroller' alebo jeho suvisiacich modelov
    pendingReviews: StrollerUserReviewEntity[]; // recenzie kocika 'stroller' cakajuce na schvalenie
    alikeStrollers: StrollerEntity[]; // suvisiace kociky s podobnymi vlastnostami ako 'stroller'
    maxReviewsToShow?: number;
}

type Props = OwnProps;

export const StrollerDetailReviews: React.FunctionComponent<Props> = React.memo(
    ({ className, stroller, reviews, pendingReviews, alikeStrollers, maxReviewsToShow = 2 }) => {
        // poradie hodnoteni ma byt:
        //   1. najskor schvalene hodnotenia z kocika 'stroller'
        //   2. potom hotnonia 'strolleru' cakajuce na schvalenie
        //   3. potom hodnotenia z pribuznych modelov
        const isReviewFromAlikeModel = (r: StrollerUserReviewEntity) =>
            r.strollerId !== stroller.id;
        const reviewsToShow = [
            ...reviews.filter((r) => !isReviewFromAlikeModel(r)),
            ...pendingReviews,
            ...reviews.filter((r) => isReviewFromAlikeModel(r)),
        ].slice(0, maxReviewsToShow);

        // do poctu hodnoteni sa nezapocitavaju hodnotenia cakajuce na schvalenie
        // (aby zobrazene cislo sedelo s poctom hodnoteni uvedenych v hlavicke detailu kocika)
        const hasShowMore = reviews.length > maxReviewsToShow;
        const hasShowMoreCount = reviews.length - maxReviewsToShow;

        return (
            <div className={cx(styles.StrollerDetailReviews, className)}>
                <div className={styles.StrollerDetailReviews__textBox}>
                    <h2 className={styles.StrollerDetailReviews__headline}>
                        {STROLLER_REVIEWS_FROM_MOTHERS}
                    </h2>
                </div>

                {reviewsToShow.length > 0 ? (
                    <div className={styles.StrollerDetailReviews__reviews}>
                        {reviewsToShow.map((review) => (
                            <CollapsedReview
                                key={review.id}
                                className={styles.StrollerDetailReviews__review}
                                stroller={stroller}
                                review={review}
                                reviewStroller={alikeStrollers.find(
                                    (s) => s.id === review.strollerId,
                                )}
                            />
                        ))}
                    </div>
                ) : (
                    <div className={styles.StrollerDetailReviews__noReviews}>
                        {STROLLER_NO_REVIEWS}
                    </div>
                )}

                <div
                    className={cx(
                        styles.StrollerDetailReviews__footer,
                        hasShowMore && styles["StrollerDetailReviews__footer--more"],
                    )}
                >
                    {hasShowMore && (
                        <Link
                            to={url(strollersReviewsUrl, { slug: stroller.slug })}
                            className={styles.StrollerDetailReviews__expand}
                        >
                            {STROLLER_MORE_REVIEWS}
                            {` (${hasShowMoreCount})`}
                        </Link>
                    )}

                    <Btn
                        label={STROLLER_ADD_REVIEW}
                        link={url(strollersAddReviewUrl, { slug: stroller.slug })}
                    />
                </div>
            </div>
        );
    },
);

StrollerDetailReviews.displayName = "StrollerDetailReviews";
